// The packing move (myta-streams D-58, `docs/plans/2026-09-15-economy-mode-seat-packing-design.md` §4). The pool
// found this user alone on a GPU box it keeps paying for, and a ready seat with the same build on another running
// box; it holds that seat and proposes the move on the victim lease's renew. This page executes it, and the ORDER
// is the operator's hard requirement: the seat the user is on is never let go before the second one is in the
// venue and framing — the user keeps watching the first stream the whole time and sees one cut, no loader.
//
// This is the pure half — what a renew's `move` means, whether a move may start, when the switch may happen, when
// to give up — kept free of React so it is unit-testable. The provider owns the peers, the leases and the switch.
//
// The contract (myta-streams api/leases.ts `moveSurface`, the claim branch, lease-contract.ts):
//   renew 200 → { move: { moveId, to: { nodeId, slotIndex, streamerId, signallingUrl, region }, proposedAt,
//                         deadline, savesUsdPerHour, reason: 'economy', hint } }        while the proposal stands
//   POST /v1/ec2/leases { eventId, userId, …, moveId, fromLeaseId } → 201 lease B (the held seat, granted directly)
//                                                                   | 409 move_gone { phase: move_gone | move_expired | hold_gone }
//                                                                   | 403 move_not_yours
//   DELETE /v1/ec2/leases/{A}?reason=moved          once the venue is up and framing on B — the saving starts here
//   DELETE /v1/ec2/leases/{B}?reason=move-failed    when B is not in the venue by `deadline` — the user stays on A

import type { IMoveProposal, IMoveTarget } from '@services/MytaStreamsService';

export type { IMoveProposal, IMoveTarget };

export type MovePhase = 'claiming' | 'connecting' | 'entering' | 'switching';

/**
 * What proved the user is standing in the venue on the new seat, weakest to strongest. Each one is real; they differ in
 * how much of the world is guaranteed to be ON SCREEN at that moment, which is the only thing the switch cares about.
 *
 * `server` — lease B's renew answered `sessionReady`, which reads the slot row's map. UE writes that when the LEVEL IS
 * LOADED. Measured on the move of 2026-09-17 09:44Z: `LoadMap` completed at 09:44:57.99 and UE only called itself ready
 * at 09:45:04 — six seconds in which the encoder is sending a world that is still assembling itself.
 *
 * `client-ready` — UE announced CLIENT_READY on the move's own websocket session. UE sends it behind its own readiness
 * gate (`pawnReady` + `avatarLoaded` + `pawnGrounded`, all 1 in that same log), so the pawn exists, stands on a floor
 * and wears its avatar.
 *
 * `ue` — UE announced TELEPORT_FINISHED on that session: the entry is complete, the same proof the ordinary entry
 * ladder treats as "the user is in" (entryWatch.ts).
 */
export type VenueProof = 'server' | 'client-ready' | 'ue';

const VENUE_PROOF_RANK: Readonly<Record<VenueProof, number>> = {
  server: 0,
  'client-ready': 1,
  ue: 2,
};

/** True when `next` says more about what is on screen than `current` — a stronger proof shortens the settle below. */
export const isStrongerVenueProof = (
  next: VenueProof,
  current: VenueProof,
): boolean => VENUE_PROOF_RANK[next] > VENUE_PROOF_RANK[current];

export interface ISeatMoveProgress {
  phase: MovePhase;
  /** When the INIT went out on the new peer, or null while it has not. */
  initSentAt: number | null;
  /** The INIT has been re-sent once already — the nudge is once per move, never a loop. */
  nudged: boolean;
  /** How and when the venue was proved on the new seat, with the frame count at that moment; null until it is. */
  venue: { by: VenueProof; at: number; frames: number } | null;
  /** Decoded frames counted on the new peer. */
  frames: number;
}

/** What the rest of the page may read about a move in flight; written on phase changes, never per frame. */
export interface ISeatMoveView {
  moveId: string;
  phase: MovePhase;
  fromLeaseId: string;
  to: IMoveTarget;
  /** The websocket session key the new UE was INIT-ed with — the hub adopts it at the switch, not before. */
  sessionKey: string;
  deadline: number;
  entered: boolean;
  /** What proved the venue on the new seat, or null while nothing has. */
  venueBy: VenueProof | null;
  frames: number;
}

/** Release the new seat this long before the server's deadline, so the `move-failed` lands as ours and not as an expiry. */
export const MOVE_ABORT_LEAD_MS = 5_000;

/**
 * Frames of THE VENUE the new peer must decode before the switch — counted from the venue proof, not from the first
 * frame of the connection. Frames before the proof are the new UE's login map and its level load; cutting to those
 * is what the user sees as "I was shown the login screen and watched the world draw itself" (2026-09-17). Thirty
 * frames is about a second at the slots' 30 fps: enough that the first thing on screen is the settled world.
 */
export const MOVE_MIN_FRAMES_AFTER_VENUE = 30;

/**
 * How long after the venue proof the switch waits, per proof (see VenueProof). The weaker the proof, the more of the
 * world may still be drawing itself, so the longer the wait. The server's proof waits out the whole gap UE needed
 * between a loaded level and its own readiness in the 2026-09-17 measurement, which is what makes a move still
 * complete — just later — when this page never hears UE at all.
 */
export const MOVE_VENUE_SETTLE_MS: Readonly<Record<VenueProof, number>> = {
  ue: 1_000,
  'client-ready': 5_000,
  server: 15_000,
};

/** Lease B's renew cadence during the move: keeps the second seat alive and carries the server's `sessionReady`. */
export const MOVE_RENEW_INTERVAL_MS = 5_000;

/** How often the move is looked at. The verdicts are second-grained; this only has to be finer. */
export const MOVE_WATCH_POLL_MS = 500;

/** When the INIT payload cannot be minted (player-session mint empty), try again after this long. */
export const MOVE_INIT_RETRY_MS = 6_000;

/** No venue this long after the INIT went out on the new peer → send it once more (entryWatch's VENUE_NUDGE_MS twin). */
export const MOVE_INIT_NUDGE_MS = 45_000;

/**
 * A streamer listed at the gateway that sends no offer: how long the hidden peer waits for its data channel before it
 * is rebuilt (the KI-37 watchdog's twin for the second peer). Measured 2026-09-15 15:39Z: a destination started 26 s
 * before the proposal stayed silent for the whole 108 s the move had — a rebuild is the only lever the page owns.
 */
export const MOVE_OFFER_TIMEOUT_MS = 20_000;

/** Hidden-peer rebuilds per move; two, like the main peer's budget, inside the same deadline. */
export const MOVE_MAX_PEER_REBUILDS = 2;

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null;

const str = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() ? v : null;

/** The renew's `move`, validated; null when absent, malformed or already past its deadline. */
export function proposalFromRenew(
  renewed: { move?: unknown },
  nowMs: number,
): IMoveProposal | null {
  const raw = renewed.move;
  if (!isRecord(raw) || !isRecord(raw.to)) {
    return null;
  }
  const moveId = str(raw.moveId);
  const deadline = str(raw.deadline);
  const nodeId = str(raw.to.nodeId);
  const slotIndex =
    typeof raw.to.slotIndex === 'number'
      ? String(raw.to.slotIndex)
      : str(raw.to.slotIndex);
  const streamerId = str(raw.to.streamerId);
  const signallingUrl = str(raw.to.signallingUrl);
  if (
    !moveId ||
    !deadline ||
    !nodeId ||
    !slotIndex ||
    !streamerId ||
    !signallingUrl
  ) {
    return null;
  }
  const deadlineMs = Date.parse(deadline);
  if (!Number.isFinite(deadlineMs) || deadlineMs <= nowMs) {
    return null;
  }
  return {
    moveId,
    to: {
      nodeId,
      slotIndex,
      streamerId,
      signallingUrl,
      region: str(raw.to.region) ?? '',
    },
    proposedAt: str(raw.proposedAt) ?? undefined,
    deadline,
    savesUsdPerHour:
      typeof raw.savesUsdPerHour === 'number' ? raw.savesUsdPerHour : undefined,
    reason: str(raw.reason) ?? undefined,
    hint: str(raw.hint) ?? undefined,
  };
}

export interface IMoveStartInput {
  /** A move is already in flight on this page. */
  inFlight: boolean;
  /** This very proposal was already refused by the server (move_gone) — do not ask again every renew. */
  declined: boolean;
  /** The user is confirmed IN the venue on the current seat (entryWatch's `enteredRef`). */
  entered: boolean;
  /** The current stream is on screen. */
  playing: boolean;
  /** The page is already leaving this seat for another reason (spot handoff, node gone, seat lost). */
  handoff: boolean;
  /** The box under the current seat is being reclaimed — the spot path owns that move. */
  draining: boolean;
  /** The visit is over. */
  ended: boolean;
}

export interface IMoveStartVerdict {
  start: boolean;
  why: string;
}

// A move may only start from a seat that is fully working. The server already refuses to pick a seat younger than
// `minSeatAgeSec`, but the page knows more than the server: whether the user is actually in the venue and looking at
// a picture. Moving someone who is still entering would trade one loading screen for two.
export function shouldStartMove(input: IMoveStartInput): IMoveStartVerdict {
  if (input.ended) {
    return { start: false, why: 'the visit is over' };
  }
  if (input.inFlight) {
    return { start: false, why: 'a move is already in flight' };
  }
  if (input.declined) {
    return { start: false, why: 'this proposal was already refused' };
  }
  if (input.handoff) {
    return { start: false, why: 'this page is already leaving the seat' };
  }
  if (input.draining) {
    return {
      start: false,
      why: 'the box is being reclaimed — the spot handoff owns this seat',
    };
  }
  if (!input.entered) {
    return { start: false, why: 'the user is not in the venue yet' };
  }
  if (!input.playing) {
    return { start: false, why: 'the current stream is not on screen yet' };
  }
  return { start: true, why: 'in the venue and playing' };
}

export type MoveAction = 'wait' | 'nudge' | 'switch' | 'abort';

export interface IMoveVerdict {
  action: MoveAction;
  why: string;
}

const sec = (ms: number): number => Math.round(ms / 1000);

export const describeVenueProof = (by: VenueProof): string =>
  by === 'ue'
    ? 'UE announced the entry on the new seat'
    : by === 'client-ready'
    ? 'UE reported itself ready in the venue on the new seat'
    : "the server reported the new seat's level loaded";

// The ladder for a move in flight, as a pure function of the progress and two clocks. `abort` outranks everything —
// past the deadline the server frees the hold and drops the proposal whatever this page thinks.
//
// `switch` needs the venue AND frames OF THAT VENUE, because each alone lies: UE can announce the venue while the
// encoder is still dark, and frames alone are a login screen until the venue is up (the 2026-09-14 lesson behind
// entryWatch.ts). The frame count is taken from the moment of the proof — before it, every frame the new peer
// decodes is the second UE's login map or its level load, and cutting to those is precisely the seam the operator
// reported on 2026-09-17: "I still see the login map and the world drawing itself". The weaker, server-side proof
// waits longest (MOVE_VENUE_SETTLE_MS), the strongest one barely at all.
export function moveVerdict(
  progress: ISeatMoveProgress,
  deadlineMs: number,
  now: number,
): IMoveVerdict {
  const left = deadlineMs - now;
  const venue = progress.venue;
  if (left <= MOVE_ABORT_LEAD_MS) {
    return {
      action: 'abort',
      why: `deadline in ${Math.max(
        0,
        sec(left),
      )} s — the new seat is not ready (${
        venue ? describeVenueProof(venue.by) : 'not in the venue'
      }, ${progress.frames} frame${progress.frames === 1 ? '' : 's'})`,
    };
  }
  if (venue) {
    const framesOfVenue = Math.max(0, progress.frames - venue.frames);
    const settleMs = MOVE_VENUE_SETTLE_MS[venue.by];
    const settled = now - venue.at >= settleMs;
    if (framesOfVenue >= MOVE_MIN_FRAMES_AFTER_VENUE && settled) {
      return {
        action: 'switch',
        why: `${describeVenueProof(
          venue.by,
        )} and ${framesOfVenue} frames of the venue decoded`,
      };
    }
    return {
      action: 'wait',
      why: `${progress.phase}: ${describeVenueProof(
        venue.by,
      )} — waiting for it to be drawn (${framesOfVenue}/${MOVE_MIN_FRAMES_AFTER_VENUE} frames${
        settled
          ? ''
          : `, ${sec(Math.max(0, venue.at + settleMs - now))} s settle`
      }) (${sec(left)} s left)`,
    };
  }
  if (
    progress.initSentAt !== null &&
    !progress.nudged &&
    now - progress.initSentAt >= MOVE_INIT_NUDGE_MS
  ) {
    return {
      action: 'nudge',
      why: `no venue ${sec(
        now - progress.initSentAt,
      )} s after the INIT on the new seat — sending it again`,
    };
  }
  const missing = [
    progress.initSentAt === null ? 'INIT not out yet' : 'venue pending',
    `${progress.frames} frame${progress.frames === 1 ? '' : 's'} so far`,
  ];
  return {
    action: 'wait',
    why: `${progress.phase}: ${missing.join(', ')} (${sec(left)} s left)`,
  };
}

/** UE has finished entering on that session — the strongest proof, and the one the entry ladder uses. */
const VENUE_ACTION = 'TELEPORT_FINISHED';

/** UE's own readiness gate on that session: pawn spawned, grounded, avatar on. Strong, but not the last word. */
const CLIENT_READY_ACTION = 'CLIENT_READY';

/**
 * What a dashboard websocket message proves about the MOVE's session, or null when it is not about it. The hub's
 * ordinary handlers skip every message whose `sessionKey` is not the current one — which is exactly what keeps the
 * user's hub state on the seat they are still looking at — so the new session's announcements have to be read here,
 * by their own key, before the switch adopts that key.
 */
export function moveEntryProof(
  action: unknown,
  sessionKey: unknown,
  expectedSessionKey: string,
): VenueProof | null {
  if (
    typeof action !== 'string' ||
    typeof sessionKey !== 'string' ||
    !expectedSessionKey ||
    sessionKey !== expectedSessionKey
  ) {
    return null;
  }
  if (action === VENUE_ACTION) {
    return 'ue';
  }
  if (action === CLIENT_READY_ACTION) {
    return 'client-ready';
  }
  return null;
}

/**
 * The hidden peer's element: full-viewport so the stream is negotiated and sized exactly as the visible one will
 * be (the KI-70 resolution cap measures the element), invisible and untouchable so nothing reaches the second UE
 * before the switch. Attached to the document because a video element outside it is not guaranteed to decode.
 */
export const SHADOW_PEER_STYLE: Readonly<Record<string, string>> = {
  position: 'fixed',
  inset: '0',
  width: '100vw',
  height: '100vh',
  opacity: '0',
  pointerEvents: 'none',
  zIndex: '-1',
};

export const describeMoveView = (view: ISeatMoveView, now: number): string =>
  `move ${view.moveId} ${view.phase} → ${view.to.nodeId}/${
    view.to.slotIndex
  } (${view.entered ? `in venue (${view.venueBy})` : 'entering'}, ${
    view.frames
  } frames, ${sec(view.deadline - now)} s left)`;
